import React, { useState, useEffect, useRef, useMemo } from "react";
import { Canvas } from "@react-three/fiber";
import CameraBackground from "../../components/CameraBackground";
import SphereEnv from "./ShpereEnv";
import HotspotModal from "./hotspots/HotspotModal";
import HotspotManager from "./hotspots/HotspotManager";
import { useHotspotTour } from "./hotspots/useHotspotTour";
import { HOTSPOTS } from "./hotspots/hotspotData";
import LandingPageInt from "../../components/LandingPageInt";
import Controls from "../../components/Controls";
import HotspotCounter from "./hotspots/HotspotCounter";
import VisitSiteButton from "../../components/VisitSiteButton";
import FlightButton from "../../components/FlightButton";
import OverlayScreen from "../../components/OverlayScreen";
import LandingDesktopQR from "../../components/LandingDesktopQR";
import useIsDesktop from "../../hooks/useIsDesktop";
import useResourcePreloader from "../../hooks/useResourcePreloader";
import { INTERIOR_RESOURCES } from '../../assetsDictionary';
import { INT_STATE } from '../../states';

export default function InteriorScene() {
    const [flightState, setFlightState] = useState(INT_STATE.LANDED);
    const isDesktop = useIsDesktop();
    
    // Preloader - memorizar el array de recursos para evitar re-renders innecesarios
    const interiorResources = useMemo(() => {
        const values = Object.values(INTERIOR_RESOURCES);
        return values;
    }, []);
    const { progress, isComplete } = useResourcePreloader(interiorResources);
    
    const [showExperience, setShowExperience] = useState(false);
    const [showCamera, setShowCamera] = useState(true);
    const [hasCameraAccess, setHasCameraAccess] = useState(true);
    const [open, setOpen] = React.useState(true);
    const [shouldFadeOut, setShouldFadeOut] = useState(false);
    const [shouldFadeIn, setShouldFadeIn] = useState(false);

    // Tiempo mínimo de visualización del overlay: 3000ms
    const startTimeRef = useRef(Date.now());
    const hasClosedOverlayRef = useRef(false);
    const MIN_DISPLAY_TIME = 3000;

    // Detectar modo edición desde URL
    const [isEditMode, setIsEditMode] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('edit') === 'true';
    });

    // Hotspot tour: estado y lógica en useHotspotTour (loop = reiniciar a 0/5 al cerrar 5/5)
    const hotspotTour = useHotspotTour(HOTSPOTS, { loop: true });

    const [playVideoKey, setPlayVideoKey] = useState(0);

    // Secuencia de estados deseada
    const STATE_SEQUENCE = [
      INT_STATE.LANDED,
      INT_STATE.TAKEOFF,
      INT_STATE.FLYING
    ];

    function advanceFlightState() {
      setFlightState(prev => {
        const idx = STATE_SEQUENCE.indexOf(prev);
        if (idx < STATE_SEQUENCE.length - 1) {
          return STATE_SEQUENCE[idx + 1];
        }
        return;
      });
    }

    // Esperar a que los recursos estén cargados Y que hayan pasado al menos MIN_DISPLAY_TIME ms
    useEffect(() => {
        if (isComplete && !hasClosedOverlayRef.current) {
            hasClosedOverlayRef.current = true;
            const elapsed = Date.now() - startTimeRef.current;
            const remainingTime = Math.max(0, MIN_DISPLAY_TIME - elapsed);
            const timer = setTimeout(() => {
                setShouldFadeOut(true);
                setOpen(false);
                setShouldFadeIn(true);
            }, remainingTime);

            return () => clearTimeout(timer);
        }
    }, [isComplete]);

    useEffect(() => {
        switch (flightState) {
            case INT_STATE.TAKEOFF:
                setPlayVideoKey((k) => k + 1); // 👈 dispara el video
            break;
            case INT_STATE.LANDED:
            case INT_STATE.FLYING:
            default:
            /**
             * TODO: 
             * El segundo video se reproduce automaticamente al terminar el primero. En realidad
             * habria que cambiar de estado cuando termina el primer video y darle play al segundo
             * en el caso state flying
             */
            break;
        }
    }, [flightState]);

    const handleEnterExperience = (hasPermissions = true) => {
        // Solo permitir entrar si los recursos están cargados
        if (!isComplete) return;
        setHasCameraAccess(hasPermissions);
        setShowExperience(true);
    };

    const handleAnimationComplete = () => {
        setShowCamera(false); 
    };

    return (
        <div>
            {isDesktop && <LandingDesktopQR qrImageSrc="../images/components/LandingDesktopQR/qr-dummy.png" />}
            {!showExperience && !isDesktop && (
                <>
                <OverlayScreen
                    open={open}
                    onClose={() => setOpen(false)}
                    className={shouldFadeOut ? "overlay--fade-out" : ""}
                    progress={!isComplete ? progress : undefined}
                />
                <LandingPageInt 
                    onEnterExperience={handleEnterExperience} 
                    shouldFadeIn={shouldFadeIn} 
                    onPermissionsChange={setHasCameraAccess} />
                </>
            )}
            
            {showExperience && (
                <>
                    {showCamera && <CameraBackground experienceType="int" />}
                    <Canvas 
                        dpr={Math.min(window.devicePixelRatio, 2)} 
                        style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%' }}
                        camera={{ near: 0.1, far: 5000, fov: 90 }}
                    >
                        <Controls />
                        <SphereEnv 
                            onIntroAnimationComplete={handleAnimationComplete} 
                            triggerPlay={playVideoKey} 
                            hasCameraAccess={hasCameraAccess}
                        />
                        {!showCamera && (
                            <HotspotManager 
                                hotspots={HOTSPOTS}
                                loop={true}
                                currentIndex={hotspotTour.currentIndex}
                                isModalOpen={hotspotTour.isModalOpen}
                                isWaitingForNext={hotspotTour.isWaitingForNext}
                                onOpenModal={hotspotTour.openModal}
                                isEditMode={isEditMode}
                                // onHotspotUpdate={handleHotspotUpdate}
                            />
                        )}
                    </Canvas>
                    {!showCamera && <FlightButton 
                        travelInfo={{
                            initialState: INT_STATE.LANDED,
                            origin: "San Francisco",
                            destination: "San Jose",
                            city: 'San Francisco',
                            travelCarTime: "1 h 25 m",
                            travelJobyTime: "16 m",
                        }}
                        state={flightState}
                        onClick={() => {
                            advanceFlightState();
                        }}
                        onCloseModal={hotspotTour.isModalOpen ? hotspotTour.closeModal : undefined}
                        shouldAnimate={true}
                    />}
                    {!showCamera && <VisitSiteButton align="right" shouldAnimate={true} />}
                    {!showCamera && <HotspotCounter displayedCount={hotspotTour.displayedCount} total={hotspotTour.total} shouldAnimate={true} />}
                    {!showCamera && (
                        <HotspotModal
                            isOpen={hotspotTour.isModalOpen}
                            currentIndex={hotspotTour.currentIndex}
                            onClose={hotspotTour.closeModal}
                        />
                    )}
                </>
            )}
        </div>
    );
}